"use client";

import AppLogo from "@/components/ui/AppLogo";
import { useLanguage } from "@/contexts/LanguageContext";

interface ServiceItem {
  name: string;
  price: string;
  desc?: string;
}

interface PricingCategory {
  length: string;
  title: string;
  items: ServiceItem[];
}

export default function PricingSection() {
  const { t } = useLanguage();

  const baseLengths: PricingCategory[] = [
    {
      length: t('pricing.50cm'),
      title: t('pricing.nordicBase'),
      items: [{ name: "100g", price: "1,950 AED" }],
    },
    {
      length: t('pricing.55cm'),
      title: t('pricing.scandinavianSignature'),
      items: [{ name: "100g", price: "2,350 AED" }],
    },
    {
      length: t('pricing.60cm'),
      title: t('pricing.nordicLength'),
      items: [{ name: "100g", price: "2,750 AED" }],
    },
  ];

  const addVolume: ServiceItem[] = [
    { name: t('pricing.fjordVolume'), price: "700 AED" },
    { name: t('pricing.arcticFull'), price: "1,300 AED" },
  ];

  const maintenance: ServiceItem[] = [
    { name: t('pricing.geniusWeft'), price: "400 AED" },
    { name: t('pricing.butterflyMethod'), price: "250 AED" },
    { name: t('pricing.fullRefit'), price: "350 AED" },
  ];

  return (
    <section id="pricing" className="py-32 bg-cream">
      <div className="max-w-5xl mx-auto px-8 md:px-12">
        {/* Menu Card Container */}
        <div className="bg-white rounded-[2.5rem] shadow-luxury-lg border border-warm overflow-hidden">
          {/* Logo & Header */}
          <div className="text-center pt-16 pb-10 px-10 border-b border-warm">
            <div className="flex justify-center items-center mb-6">
              <div className="w-36 h-36 flex items-center justify-center">
                <AppLogo />
              </div>
            </div>
            <p className="text-xs tracking-[0.35em] text-gold uppercase mb-10 font-medium">{t('pricing.byScandinavia')}</p>
            <h2 className="font-serif text-4xl md:text-5xl text-charcoal tracking-wide font-normal">
              {t('pricing.copenhagenCollection')}
            </h2>
          </div>

          {/* Pricing Content */}
          <div className="px-10 md:px-16 py-14">

            {/* BASE LENGTHS */}
            <div className="mb-14">
              <div className="mb-8">
                <h3 className="font-serif text-xl md:text-2xl text-charcoal mb-2 font-medium tracking-widest uppercase text-gold">
                  Base Lengths
                </h3>
                <div className="w-20 h-0.5 bg-gold mt-3 rounded-full" />
              </div>
              <div className="space-y-5">
                {baseLengths.map((cat, idx) => (
                  <div key={idx} className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 sm:gap-4">
                    <span className="text-base md:text-lg text-charcoal/80 font-light break-words">
                      {cat.length} — {cat.title} — {cat.items[0].name}
                    </span>
                    <div className="hidden sm:block flex-1 border-b border-dotted border-gold/30 mb-2" />
                    <span className="text-base md:text-lg text-charcoal font-semibold">
                      {cat.items[0].price}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="my-10 border-t border-warm" />

            {/* ADD VOLUME */}
            <div className="mb-14">
              <div className="mb-8">
                <h3 className="font-serif text-xl md:text-2xl text-charcoal mb-2 font-medium tracking-widest uppercase text-gold">
                  {t('pricing.addVolume')}
                </h3>
                <div className="w-20 h-0.5 bg-gold mt-3 rounded-full" />
              </div>
              <div className="space-y-5">
                {addVolume.map((item, idx) => (
                  <div key={idx} className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 sm:gap-4">
                    <span className="text-base md:text-lg text-charcoal/80 font-light break-words">
                      + {item.name}
                    </span>
                    <div className="hidden sm:block flex-1 border-b border-dotted border-gold/30 mb-2" />
                    <span className="text-base md:text-lg text-charcoal font-semibold">
                      {item.price}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="my-10 border-t border-warm" />

            {/* SCANDIC VOLUME BOOST */}
            <div className="mb-14">
              <div className="mb-8">
                <h3 className="font-serif text-xl md:text-2xl text-charcoal mb-2 font-medium tracking-widest uppercase text-gold">
                  {t('pricing.scandicVolumeBoost')}
                </h3>
                <div className="w-20 h-0.5 bg-gold mt-3 rounded-full" />
              </div>
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 sm:gap-4">
                <span className="text-base md:text-lg text-charcoal/80 font-light break-words max-w-md">
                  {t('pricing.scandicVolumeBoostDesc')}
                </span>
                <div className="hidden sm:block flex-1 border-b border-dotted border-gold/30 mb-2" />
                <span className="text-base md:text-lg text-charcoal font-semibold">
                  1,100 AED
                </span>
              </div>
            </div>

            {/* Divider */}
            <div className="my-10 border-t border-warm" />

            {/* CUSTOM LENGTHS */}
            <div className="mb-14">
              <div className="mb-8">
                <h3 className="font-serif text-xl md:text-2xl text-charcoal mb-2 font-medium tracking-widest uppercase text-gold">
                  {t('pricing.customLengths')}
                </h3>
                <div className="w-20 h-0.5 bg-gold mt-3 rounded-full" />
              </div>
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 sm:gap-4">
                <span className="text-base md:text-lg text-charcoal/80 font-light break-words">
                  {t('pricing.over60cm')}
                </span>
                <div className="hidden sm:block flex-1 border-b border-dotted border-gold/30 mb-2" />
                <span className="text-base md:text-lg text-gold font-semibold">
                  {t('pricing.customQuote')}
                </span>
              </div>
            </div>

            {/* Divider */}
            <div className="my-14 border-t border-warm" />

            {/* Refit & Maintenance */}
            <div>
              <div className="mb-8">
                <h3 className="font-serif text-2xl md:text-3xl text-charcoal mb-2 font-medium">
                  {t('pricing.refitMaintenance')}
                </h3>
                <div className="w-20 h-0.5 bg-gold mt-3 rounded-full" />
              </div>

              <div className="space-y-5">
                {maintenance.map((item, idx) => (
                  <div key={idx} className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 sm:gap-4">
                    <span className="text-base md:text-lg text-charcoal/80 font-light break-words">
                      {item.name}
                    </span>
                    <div className="hidden sm:block flex-1 border-b border-dotted border-gold/30 mb-2" />
                    <span className="text-base md:text-lg text-charcoal font-semibold">
                      {item.price}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Footer Note */}
          <div className="text-center px-10 pb-12">
            <p className="text-xs text-muted font-light leading-relaxed">
              {t('pricing.footerNote1')}<br />
              {t('pricing.footerNote2')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}